// Media daily working amplitude constraint
// For each user and each day (by event start date), prevent assignments
// that would cause the working amplitude (last end - first start across
// that day's assigned events) to exceed a cap.
//
// Modeling approach: for every pair of same-day events (a, b) whose span
// max(end(a), end(b)) - min(start(a), start(b)) exceeds amplitudeMaxHours,
// we forbid a user from being assigned to both by enforcing:
//   sum_R x_{u,a,R} + sum_R x_{u,b,R} <= 1
// This ensures the extremal pair cannot be both selected, bounding amplitude.
//
// Params:
// - amplitudeMaxHours: number (default 11)
// Requires indexes: users, eventDateYmd, eventStartMs, eventEndMs, staffRequiredByRole, decisionVariables
export function buildMediaDailyAmplitudeConstraints(model, params = {}) {
  const idx = model?.optimizationIndexes || {};
  const users = Object.keys(idx.users || {});
  const eventDateYmd = idx.eventDateYmd || {};
  const startMs = idx.eventStartMs || {};
  const endMs = idx.eventEndMs || {};
  const byRole = idx.staffRequiredByRole || {};
  const declared = new Set(Object.keys(idx.decisionVariables || {}));

  const amplitudeMaxHours = Number(params?.amplitudeMaxHours) > 0 ? Number(params.amplitudeMaxHours) : 11;
  const amplitudeMaxMs = amplitudeMaxHours * 3600000;

  // Group events by day key ymd
  const byDay = new Map(); // ymd -> [{ eKey, s, e }]
  for (const [eKey, ymd] of Object.entries(eventDateYmd)) {
    const s = startMs[eKey];
    const e = endMs[eKey];
    if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
    if (!byDay.has(ymd)) byDay.set(ymd, []);
    byDay.get(ymd).push({ eKey, s, e });
  }

  const constraints = [];

  for (const [, itemsRaw] of byDay.entries()) {
    const items = itemsRaw.slice();
    const n = items.length;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = items[i];
        const b = items[j];
        const earliestStart = Math.min(a.s, b.s);
        const latestEnd = Math.max(a.e, b.e);
        const span = latestEnd - earliestStart;
        if (!Number.isFinite(span) || span <= amplitudeMaxMs) continue;
        // Build pairwise constraint for each user: cannot take both a and b
        for (const uKey of users) {
          const rolesA = Object.keys(byRole[a.eKey] || {});
          const rolesB = Object.keys(byRole[b.eKey] || {});
          const vars = [
            ...rolesA.map((r) => `x_${uKey}_${a.eKey}_${r}`),
            ...rolesB.map((r) => `x_${uKey}_${b.eKey}_${r}`),
          ].filter((name) => declared.has(name));
          if (!vars.length) continue;
          const coeffs = vars.map(() => 1);
          constraints.push({ linear: { vars, coeffs }, domain: [-100000, 1] });
        }
      }
    }
  }

  return constraints;
}

